import React from 'react';
import { ShippingMethod } from '../types';

interface ShippingMethodSelectorProps {
  shippingMethods: ShippingMethod[];
  selectedMethod?: ShippingMethod;
  onMethodChange: (method: ShippingMethod) => void;
  error?: string;
}

const ShippingMethodSelector: React.FC<ShippingMethodSelectorProps> = ({
  shippingMethods,
  selectedMethod,
  onMethodChange,
  error,
}) => {
  const formatPrice = (amount: number | undefined | null, currency: string) => {
    const safeAmount = typeof amount === 'number' && isFinite(amount) ? amount : 0;
    if (safeAmount === 0) return null; // Will be rendered as "FREE"
    const locale = currency.toLowerCase() === 'sek' ? 'sv-SE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(safeAmount / 100);
  };

  if (shippingMethods.length === 0) {
    return (
      <div 
        className="shipping-methods" 
        data-testid="shipping-methods"
        role="region"
        aria-label="Shipping methods"
      >
        <div className="no-methods">
          <p>No shipping methods available for your location.</p>
          <p>Please contact support for assistance.</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="shipping-methods" 
      data-testid="shipping-methods"
      role="region"
      aria-label="Shipping methods"
    >
      {error && <div className="error-text" role="alert">{error}</div>}
      
      <div className="shipping-options">
        {shippingMethods.map((method) => (
          <label 
            key={method.id} 
            className="shipping-option"
            data-testid="shipping-option"
          >
            <input
              type="radio"
              name="shipping_method"
              value={method.id}
              checked={selectedMethod?.id === method.id}
              onChange={() => onMethodChange(method)}
              aria-label={`${method.name} - ${formatPrice(method.price, method.currency_code) || 'FREE'}`}
            />
            <div className="shipping-option-content">
              <div className="shipping-option-info">
                <h4>{method.name}</h4>
                {method.description && (
                  <p className="shipping-description">{method.description}</p>
                )}
              </div>
              <div className="shipping-option-price">
                {formatPrice(method.price, method.currency_code) ? (
                  <span className="shipping-price" data-testid="shipping-price">
                    {formatPrice(method.price, method.currency_code)}
                  </span>
                ) : (
                  <span className="free-shipping" data-testid="shipping-price">FREE</span>
                )}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

export default ShippingMethodSelector;